import { atom } from "jotai";
import type {
  AskHelenaInterruptedTurn,
  AskHelenaMessage,
  AskHelenaMessageFeedback,
  AskHelenaPendingWrite,
  AskHelenaPendingWriteStatus,
  AskHelenaStatus,
  AskHelenaTicketRef,
} from "shared";
import { debugConsole } from "utilz/helpers";
import { v4 as uuidv4 } from "uuid";

/**
 * The live Ask Helena turn. One writer per state transition, so the UI can
 * never compose an impossible state:
 *
 *         send()
 * idle/error ──► thinking ──tool_call──► tool_running
 *                   ▲                         │
 *                   └──────tool_result────────┘   (ok:false ALSO returns here)
 *                   │
 *              token (first)          awaiting_confirmation
 *                   ▼                         ▼
 *               answering ──token(n)──►   awaiting_confirmation
 *                                             │
 *                            confirm()/cancel()/send()
 *                                             ▼
 *                                          thinking
 *
 * `send()` from awaiting_confirmation is the "change it" path: the open card is
 * marked `superseded` and a revised one arrives with the next answer.
 *
 * error event / throw ──► error      done ──► idle      stop() ──► idle
 *
 * `done` is NOT unconditionally terminal. It also arrives after `error` (which
 * it must not wipe) and after `awaiting_confirmation` (where the turn is
 * SUSPENDED, waiting on a human, not finished).
 */

// ─── Bases (private) ────────────────────────────────────────────────────────
const askHelenaMessagesAtom = atom<AskHelenaMessage[]>([]);
const askHelenaStatusAtom = atom<AskHelenaStatus>("idle");
const askHelenaConversationIdAtom = atom<string | null>(null);
const askHelenaActiveToolAtom = atom<string | null>(null);
const askHelenaErrorAtom = atom<string | null>(null);
const askHelenaSessionIdAtom = atom<string>(uuidv4());
const askHelenaInterruptedTurnAtom = atom<AskHelenaInterruptedTurn | null>(
  null,
);

const isStreamingStatus = (status: AskHelenaStatus): boolean =>
  status === "thinking" || status === "tool_running" || status === "answering";

// ─── Reads ──────────────────────────────────────────────────────────────────
export const askHelenaMessages = atom((get) => get(askHelenaMessagesAtom));
export const askHelenaStatus = atom((get) => get(askHelenaStatusAtom));
export const askHelenaConversationId = atom((get) =>
  get(askHelenaConversationIdAtom),
);
export const askHelenaActiveTool = atom((get) => get(askHelenaActiveToolAtom));
export const askHelenaError = atom((get) => get(askHelenaErrorAtom));
export const askHelenaInterruptedTurn = atom((get) =>
  get(askHelenaInterruptedTurnAtom),
);

/**
 * Blocks sending. `awaiting_confirmation` deliberately does NOT count: replying
 * instead of deciding is how a technician changes a proposal. The service
 * withdraws the open card on the next question and raises a revised one, so the
 * composer has to stay usable for that path to exist.
 */
export const isAskHelenaBusy = atom((get) =>
  isStreamingStatus(get(askHelenaStatusAtom)),
);

export const isAskHelenaStreaming = atom((get) =>
  isStreamingStatus(get(askHelenaStatusAtom)),
);

export const isAskHelenaResumable = atom((get) => {
  const interrupted = get(askHelenaInterruptedTurnAtom);
  return (
    interrupted !== null &&
    interrupted.conversationId !== null &&
    interrupted.conversationId === get(askHelenaConversationIdAtom) &&
    !isStreamingStatus(get(askHelenaStatusAtom))
  );
});

/** True while a proposal is on screen waiting on approve / decline. */
export const isAskHelenaAwaitingConfirmation = atom(
  (get) => get(askHelenaStatusAtom) === "awaiting_confirmation",
);

// ─── Writers — one per transition ───────────────────────────────────────────

/**
 * A card still waiting when the next question is asked. The service withdraws
 * it server-side and answers 404 to any later approve or decline, so the
 * buttons have to go the moment the reply is sent — not when the answer lands.
 */
const supersedePendingWrites = (
  messages: AskHelenaMessage[],
): AskHelenaMessage[] =>
  messages.map((message) =>
    message.pendingWrite && !message.isPendingWriteResolved
      ? {
          ...message,
          isPendingWriteResolved: true,
          pendingWrite: { ...message.pendingWrite, status: "superseded" },
        }
      : message,
  );

/**
 * Pushes the user message AND an empty assistant placeholder. Creating the
 * placeholder here is what lets `appendAskHelenaToken` be append-only — with no
 * create-or-append branch there is nowhere for an assignment to creep in.
 */
export const startAskHelenaTurn = atom(
  null,
  (
    get,
    set,
    payload: {
      userMessageId: string;
      assistantMessageId: string;
      question: string;
    },
  ): string => {
    set(askHelenaMessagesAtom, (prev) => [
      ...supersedePendingWrites(prev),
      { id: payload.userMessageId, role: "user", content: payload.question },
      { id: payload.assistantMessageId, role: "assistant", content: "" },
    ]);
    set(askHelenaStatusAtom, "thinking");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaErrorAtom, null);
    set(askHelenaInterruptedTurnAtom, null);
    return get(askHelenaSessionIdAtom);
  },
);

/**
 * Hydrates a thread fetched from history so the user reads it, and so the next
 * question continues it server-side rather than opening a new conversation.
 *
 * The same-id guard is the important part: the messages query can settle again
 * while a turn is in flight, and a second hydration would wipe the streaming
 * answer. Switching to a DIFFERENT conversation is the only thing that replaces
 * what is on screen.
 */
export const loadAskHelenaConversation = atom(
  null,
  (
    get,
    set,
    payload: { conversationId: string; messages: AskHelenaMessage[] },
  ) => {
    if (get(askHelenaConversationIdAtom) === payload.conversationId) return;

    const isSuspended = payload.messages.some(
      (message) => message.pendingWrite && !message.isPendingWriteResolved,
    );

    set(askHelenaMessagesAtom, payload.messages);
    set(askHelenaConversationIdAtom, payload.conversationId);
    set(askHelenaStatusAtom, isSuspended ? "awaiting_confirmation" : "idle");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaErrorAtom, null);
  },
);

export const rehydrateAskHelenaConversation = atom(
  null,
  (
    get,
    set,
    payload: {
      conversationId: string;
      messages: AskHelenaMessage[];
      isSettled: boolean;
    },
  ): boolean => {
    if (get(askHelenaConversationIdAtom) !== payload.conversationId) {
      return false;
    }
    if (isStreamingStatus(get(askHelenaStatusAtom))) return false;
    if (!payload.isSettled || payload.messages.length === 0) return false;

    const isSuspended = payload.messages.some(
      (message) => message.pendingWrite && !message.isPendingWriteResolved,
    );

    set(askHelenaMessagesAtom, payload.messages);
    set(askHelenaStatusAtom, isSuspended ? "awaiting_confirmation" : "idle");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaErrorAtom, null);
    return true;
  },
);

export const markAskHelenaTurnInterrupted = atom(
  null,
  (get, set, sessionId: string): boolean => {
    if (get(askHelenaSessionIdAtom) !== sessionId) return false;
    if (!isStreamingStatus(get(askHelenaStatusAtom))) return false;

    const messages = get(askHelenaMessagesAtom);
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return false;

    set(askHelenaInterruptedTurnAtom, {
      conversationId: get(askHelenaConversationIdAtom),
      assistantMessageId: last.id,
      sessionId,
      interruptedAt: Date.now(),
    });
    set(askHelenaStatusAtom, "idle");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaMessagesAtom, (prev) => {
      const target = prev[prev.length - 1];
      if (!target || target.role !== "assistant") return prev;
      return [...prev.slice(0, -1), { ...target, isInterrupted: true }];
    });
    return true;
  },
);

export const clearAskHelenaInterruptedTurn = atom(null, (_get, set) => {
  set(askHelenaInterruptedTurnAtom, null);
});

export const setAskHelenaConversationId = atom(
  null,
  (get, set, payload: { sessionId: string; conversationId: string }) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    const current = get(askHelenaConversationIdAtom);
    if (current && current !== payload.conversationId) {
      debugConsole(
        "Ask Helena: conversationId changed mid-session",
        current,
        payload.conversationId,
      );
    }
    set(askHelenaConversationIdAtom, payload.conversationId);
  },
);

/**
 * APPEND, never assign. Today the whole answer arrives in one `token` event,
 * but incremental streaming ships later with the same event shape — appending
 * makes this code correct in both worlds. The functional update also means N
 * tokens landing in one tick cannot clobber each other.
 */
export const appendAskHelenaToken = atom(
  null,
  (get, set, payload: { sessionId: string; text: string }) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    set(askHelenaStatusAtom, "answering");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaMessagesAtom, (prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      return [
        ...prev.slice(0, -1),
        { ...last, content: last.content + payload.text },
      ];
    });
  },
);

/**
 * `label` — the service's own human phrase, e.g. "Checking the customer list".
 * Never `name`: `deskday_list_customers` exists for correlation and logging and
 * must not reach the DOM.
 */
export const startAskHelenaTool = atom(
  null,
  (get, set, payload: { sessionId: string; label: string }) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    set(askHelenaStatusAtom, "tool_running");
    set(askHelenaActiveToolAtom, payload.label);
  },
);

/**
 * `ok` is deliberately not a parameter: a denied or failed tool is a NORMAL
 * outcome that the prose answer explains. There is intentionally no edge from
 * here into `failAskHelenaTurn` — an error banner beside a good explanation
 * reads as a bug to the user.
 */
export const finishAskHelenaTool = atom(null, (get, set, sessionId: string) => {
  if (get(askHelenaSessionIdAtom) !== sessionId) return;

  set(askHelenaStatusAtom, "thinking");
  set(askHelenaActiveToolAtom, null);
});

export const failAskHelenaTurn = atom(
  null,
  (get, set, payload: { sessionId: string; message: string }) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    set(askHelenaStatusAtom, "error");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaErrorAtom, payload.message);
  },
);

/**
 * A change was proposed. The turn SUSPENDS here: `done` follows immediately but
 * the thread is waiting on a human, not finished.
 *
 * The proposal is attached to the assistant message rather than held in an atom
 * of its own, so it stays anchored to the point in the thread it belongs to
 * once the conversation moves on.
 */
export const suspendAskHelenaTurn = atom(
  null,
  (
    get,
    set,
    payload: { sessionId: string; pendingWrite: AskHelenaPendingWrite },
  ) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    set(askHelenaStatusAtom, "awaiting_confirmation");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaMessagesAtom, (prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      return [
        ...prev.slice(0, -1),
        { ...last, pendingWrite: payload.pendingWrite },
      ];
    });
  },
);

const clearPendingWrite = (
  messages: AskHelenaMessage[],
  pendingWriteId: string,
  status?: AskHelenaPendingWriteStatus,
): AskHelenaMessage[] =>
  messages.map((message) =>
    message.pendingWrite?.pendingWriteId === pendingWriteId
      ? {
          ...message,
          isPendingWriteResolved: true,
          pendingWrite: status
            ? { ...message.pendingWrite, status }
            : message.pendingWrite,
        }
      : message,
  );

/**
 * The user approved or declined: the card's buttons go and the turn resumes
 * streaming into the SAME assistant message, so the answer continues where the
 * proposal interrupted it.
 *
 * The card itself is kept, marked resolved — it is the record of what was
 * agreed to, and removing it would leave the resulting change unexplained.
 */
export const resumeAskHelenaTurn = atom(
  null,
  (
    get,
    set,
    payload: {
      pendingWriteId: string;
      status: AskHelenaPendingWriteStatus;
    },
  ): string => {
    set(askHelenaStatusAtom, "thinking");
    set(askHelenaActiveToolAtom, null);
    set(askHelenaErrorAtom, null);
    set(askHelenaMessagesAtom, (prev) => {
      const next = clearPendingWrite(
        prev,
        payload.pendingWriteId,
        payload.status,
      );
      const last = next[next.length - 1];
      return last?.role === "assistant"
        ? next
        : [...next, { id: uuidv4(), role: "assistant", content: "" }];
    });
    return get(askHelenaSessionIdAtom);
  },
);

/**
 * The proposal is no longer actionable — a 404 on confirm/cancel (already
 * acted on, expired, never existed: the service refuses to distinguish them),
 * or 15 minutes elapsed. The turn stops being suspended so the user can ask
 * again, which is the only way forward.
 */
export const resolveAskHelenaPendingWrite = atom(
  null,
  (
    get,
    set,
    payload: {
      sessionId: string;
      pendingWriteId: string;
      status?: AskHelenaPendingWriteStatus;
    },
  ) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    if (get(askHelenaStatusAtom) === "awaiting_confirmation") {
      set(askHelenaStatusAtom, "idle");
    }
    set(askHelenaMessagesAtom, (prev) =>
      clearPendingWrite(prev, payload.pendingWriteId, payload.status),
    );
  },
);

export const setAskHelenaMessageId = atom(
  null,
  (
    get,
    set,
    payload: {
      sessionId: string;
      messageId: string;
      ticketRefs?: AskHelenaTicketRef[] | null;
    },
  ) => {
    if (get(askHelenaSessionIdAtom) !== payload.sessionId) return;

    set(askHelenaMessagesAtom, (prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          serverMessageId: payload.messageId,
          ticketRefs: payload.ticketRefs ?? last.ticketRefs,
        },
      ];
    });
  },
);

export const setAskHelenaMessageFeedback = atom(
  null,
  (
    _get,
    set,
    payload: {
      messageId: string;
      feedback: AskHelenaMessageFeedback | null;
    },
  ) => {
    set(askHelenaMessagesAtom, (prev) =>
      prev.map((message) =>
        message.serverMessageId === payload.messageId
          ? { ...message, feedback: payload.feedback }
          : message,
      ),
    );
  },
);

/**
 * `done` always arrives last, INCLUDING after `error` and after
 * `awaiting_confirmation` — so this must not unconditionally reset to idle. It
 * would wipe the error the user needs, or make the approval card vanish the
 * instant it appeared.
 */
export const finishAskHelenaTurn = atom(null, (get, set, sessionId: string) => {
  if (get(askHelenaSessionIdAtom) !== sessionId) return;

  const status = get(askHelenaStatusAtom);
  set(
    askHelenaStatusAtom,
    status === "error" || status === "awaiting_confirmation" ? status : "idle",
  );
  set(askHelenaActiveToolAtom, null);
});

export const stopAskHelenaTurn = atom(null, (get, set) => {
  const status = get(askHelenaStatusAtom);
  if (
    status !== "thinking" &&
    status !== "tool_running" &&
    status !== "answering"
  )
    return;

  set(askHelenaStatusAtom, "idle");
  set(askHelenaActiveToolAtom, null);
  set(askHelenaMessagesAtom, (prev) => {
    const last = prev[prev.length - 1];
    if (!last || last.role !== "assistant") return prev;
    return [...prev.slice(0, -1), { ...last, isStopped: true }];
  });
});

export const resetAskHelenaSession = atom(null, (_get, set) => {
  set(askHelenaMessagesAtom, []);
  set(askHelenaStatusAtom, "idle");
  set(askHelenaConversationIdAtom, null);
  set(askHelenaActiveToolAtom, null);
  set(askHelenaErrorAtom, null);
  set(askHelenaInterruptedTurnAtom, null);
  set(askHelenaSessionIdAtom, uuidv4());
});
